/**
 * RecordsTable - Shared table component for displaying signal records
 *
 * Used by:
 * - OutcomeRecordsDialog (Consumer Duty Outcomes)
 * - HighRiskRecords panel
 * - Future record displays in the report
 */

import { Badge } from '../../../../../ui/badge';
import { Skeleton } from '../../../../../ui/skeleton';
import { Button } from '../../../../../ui/button';
import { ConductScoreCircle } from '../../../../../ui/conduct-score-circle';
import { Timestamp } from '../../../../../ui/timestamp';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import type { HighRiskRecord } from '../../types';

/** Column width constants for consistent layout */
const COLUMN_WIDTHS = {
  SCORE: 'w-20',
  DATE: 'w-28',
  CUSTOMER: 'w-32',
  SUMMARY: '', // Flexible - takes remaining space
  CAUSES: 'w-80',
  ACTION: 'w-12',
} as const;

export interface RecordsTableProps {
  /** Array of records to display */
  records: HighRiskRecord[];
  /** Callback when a record row is clicked */
  onRecordClick: (recordId: string) => void;
  /** Callback when a cause badge is clicked */
  onCauseClick: (labelId: string, labelName: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Message to show when no records */
  emptyMessage?: string;
  /** Number of skeleton rows to show when loading */
  loadingRows?: number;
  /** Whether to use sticky header (for scrollable containers) */
  stickyHeader?: boolean;
  /** Icon to show in action column */
  actionIcon?: 'external' | 'chevron';
  /** Optional pagination config */
  pagination?: {
    page: number;
    totalPages: number;
    totalRecords: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    isLoading?: boolean;
  };
}

/**
 * Individual record row component
 */
function RecordRow({
  record,
  onClick,
  onCauseClick,
  actionIcon,
}: {
  record: HighRiskRecord;
  onClick: () => void;
  onCauseClick: (labelId: string, labelName: string) => void;
  actionIcon: 'external' | 'chevron';
}) {
  const ActionIcon = actionIcon === 'external' ? ExternalLink : ChevronRight;

  return (
    <tr
      className="cursor-pointer border-b border-border/70 transition-colors hover:bg-accent/40 last:border-b-0"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Conduct Score */}
      <td className={`py-3 pl-4 pr-2 ${COLUMN_WIDTHS.SCORE}`}>
        <ConductScoreCircle score={record.conductScore} size="sm" showDecimal />
      </td>

      {/* Date */}
      <td className={`py-3 px-2 text-sm text-muted-foreground whitespace-nowrap ${COLUMN_WIDTHS.DATE}`}>
        <Timestamp value={record.date} format="date-only" />
      </td>

      {/* Customer Name */}
      <td className={`py-3 px-2 text-sm text-foreground truncate ${COLUMN_WIDTHS.CUSTOMER}`}>
        {record.customerName || '—'}
      </td>

      {/* Summary - flexible width, 2-line clamp */}
      <td className="py-3 px-2 text-sm text-muted-foreground" title={record.summary}>
        <span className="line-clamp-2">{record.summary}</span>
      </td>

      {/* Root Causes (max 2 visible) */}
      <td className={`py-3 px-2 ${COLUMN_WIDTHS.CAUSES}`}>
        <div className="flex gap-1 flex-wrap">
          {record.rootCauseLabels.slice(0, 2).map((label) => (
        <Badge
              key={label.id}
              variant="secondary"
              className="cursor-pointer text-xs hover:bg-secondary/80"
              title={label.name}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onCauseClick(label.id, label.name);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onCauseClick(label.id, label.name);
                }
              }}
            >
              {label.name.length > 15 ? label.name.substring(0, 15) + '…' : label.name}
            </Badge>
          ))}
          {record.rootCauseLabels.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{record.rootCauseLabels.length - 2}
            </Badge>
          )}
        </div>
      </td>

      {/* Action icon */}
      <td className={`py-3 pr-4 pl-2 ${COLUMN_WIDTHS.ACTION}`}>
        <ActionIcon className="w-4 h-4 text-muted-foreground" />
      </td>
    </tr>
  );
}

/**
 * Table header component
 */
function TableHeader({ sticky }: { sticky: boolean }) {
  return (
    <thead>
      <tr
        className={`border-b border-border/70 bg-background/95 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground ${
          sticky ? 'sticky top-0' : ''
        }`}
      >
        <th className={`py-2 pl-4 pr-2 text-left font-medium ${COLUMN_WIDTHS.SCORE}`}>
          Score
        </th>
        <th className={`py-2 px-2 text-left font-medium ${COLUMN_WIDTHS.DATE}`}>Date</th>
        <th className={`py-2 px-2 text-left font-medium ${COLUMN_WIDTHS.CUSTOMER}`}>
          Customer
        </th>
        <th className="py-2 px-2 text-left font-medium">Summary</th>
        <th className={`py-2 px-2 text-left font-medium ${COLUMN_WIDTHS.CAUSES}`}>Causes</th>
        <th className={`py-2 pr-4 pl-2 ${COLUMN_WIDTHS.ACTION}`}>
          <span className="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
  );
}

/**
 * Loading skeleton for the table
 */
function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 px-4 py-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-2xl" />
      ))}
    </div>
  );
}

/**
 * Pagination controls
 */
function Pagination({
  page,
  totalPages,
  totalRecords,
  onPreviousPage,
  onNextPage,
  isLoading,
}: NonNullable<RecordsTableProps['pagination']>) {
  return (
    <div className="flex items-center justify-between border-t border-border/70 pt-4">
      <span className="text-sm text-muted-foreground">
        Page {page} of {totalPages} ({totalRecords} records)
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPreviousPage}
          disabled={page === 1 || isLoading}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNextPage}
          disabled={page >= totalPages || isLoading}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/**
 * RecordsTable - Shared table for displaying signal records
 */
export function RecordsTable({
  records,
  onRecordClick,
  onCauseClick,
  isLoading = false,
  emptyMessage = 'No records found.',
  loadingRows = 5,
  stickyHeader = false,
  actionIcon = 'chevron',
  pagination,
}: RecordsTableProps) {
  if (isLoading) {
    return <TableSkeleton rows={loadingRows} />;
  }

  if (records.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      <table className="w-full" aria-label="Records">
        <TableHeader sticky={stickyHeader} />
        <tbody>
          {records.map((record) => (
            <RecordRow
              key={record.id}
              record={record}
              onClick={() => onRecordClick(record.id)}
              onCauseClick={onCauseClick}
              actionIcon={actionIcon}
            />
          ))}
        </tbody>
      </table>

      {pagination && pagination.totalPages > 1 && (
        <Pagination {...pagination} />
      )}
    </>
  );
}
